import { useEffect, useMemo, useState } from 'react';
import type { RootState } from '@/store';
import {
  Alert,
  Box,
  Button,
  CardContent,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { createSelector } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { USER_LOAN_PROGRAM, userLoanProgramMap } from '@/config/constants';
import { useCreateLoanApplicationMutation } from '@/apis/extended/loanApplicationApi';
import { useGetProductQuery } from '@/apis/extended/productApi';
import { useGetRateMatricesQuery } from '@/apis/extended/userInterestRateApi';
import { ProductDetailEstimation } from '@/components/ProductDetail/ProductDetailEstimation';
import { GlassCard } from '@/components/ui/GlassCard';
import {
  loadAprAssumptionsFromBackend,
  loadCalculatedFeeRulesFromBackend,
  loadFeeItemsFromBackend,
  loadRateMatricesFromBackend,
} from '@/store/slices/interestRateSlice';
import { setRefinanceState } from '@/store/slices/refinanceSlice';
import { calculateHELOANClosingDisclosure } from '@/utils/calculator/HELOAN';
import { calculateHELOCClosingDisclosure } from '@/utils/calculator/HELOC';
import { calculateSecondMortgageClosingDisclosure } from '@/utils/calculator/SecondMortgage';
import { mapUserLoanProgramToLoanType } from '@/utils/interestRate/quote';
import {
  BACKEND_TO_FRONTEND_LOAN_TYPE,
  FRONTEND_TO_BACKEND_LOAN_TYPE,
  transformAllBackendMatrices,
} from '@/utils/rateMatrixUtils';
import {
  calculateBridgeRefinanceClosingDisclosure,
  calculateCommercialClosingDisclosure,
  calculateConventionalClosingDisclosure,
  calculateDefaultClosingDisclosure,
  calculateDSCRClosingDisclosure,
  calculateFHAClosingDisclosure,
  calculateFixAndFlipClosingDisclosure,
  calculateUSDAClosingDisclosure,
  calculateVAClosingDisclosure,
} from '@/utils/refinanceCalculator';
import { REFI_TYPE_LABELS } from '@/utils/refinanceDocs';
import {
  getRefinanceImpliedEquityDown,
  mergeRefinanceSliceIntoProduct,
} from '@/utils/refinanceEstimationFromProduct';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const percent = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 2,
});

const formatCurrency = (value: number) =>
  currency.format(Number.isFinite(value) ? value : 0);

const formatPercent = (value: number) =>
  percent.format(Number.isFinite(value) ? value / 100 : 0);

const RATE_SNAPSHOTS_KEY_PREFIX = 'refinance_rate_snapshots_';
const MAX_SNAPSHOTS = 50;

export type RateSnapshot = { date: string; rate: number; program?: string };

function loadRateSnapshots(productId: string | undefined): RateSnapshot[] {
  if (!productId) return [];
  try {
    const raw = localStorage.getItem(
      `${RATE_SNAPSHOTS_KEY_PREFIX}${productId}`
    );
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RateSnapshot[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveRateSnapshots(productId: string, snapshots: RateSnapshot[]) {
  try {
    localStorage.setItem(
      `${RATE_SNAPSHOTS_KEY_PREFIX}${productId}`,
      JSON.stringify(snapshots.slice(-MAX_SNAPSHOTS))
    );
  } catch (e) {
    console.error('Failed to save rate snapshots', e);
  }
}

const selectEstimationState = createSelector(
  [
    (state: RootState) => state.interestRate,
    (state: RootState) => state.hybridInterestRate,
  ],
  (interestRate, hybridInterestRate) => ({ interestRate, hybridInterestRate })
);

export const RefinanceSummaryPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { productId } = useParams();
  const [showDocs, setShowDocs] = useState(false);
  const [rateSnapshots, setRateSnapshots] = useState<RateSnapshot[]>([]);

  const refinance = useSelector((state: RootState) => state.refinance);
  const estimationState = useSelector(selectEstimationState);

  const { data: rateMatrices } = useGetRateMatricesQuery();
  const { data: product, isLoading: isProductLoading } = useGetProductQuery(
    productId ?? '',
    { skip: !productId }
  );
  const [createLoanApplication, { isLoading: creatingApprovalApp }] =
    useCreateLoanApplicationMutation();

  useEffect(() => {
    if (rateMatrices?.programs) {
      const transformedMatrices = transformAllBackendMatrices(rateMatrices);
      dispatch(loadRateMatricesFromBackend(transformedMatrices));

      const transformedAprAssumptions: Record<string, any> = {};
      const transformedFeeItems: Record<string, any> = {};
      const transformedCalculatedFeeRules: Record<string, any> = {};
      Object.entries(rateMatrices.programs).forEach(([programKey, program]) => {
        const loanType = BACKEND_TO_FRONTEND_LOAN_TYPE[programKey];
        if (!loanType) return;
        if ((program as any)?.aprAssumptions) {
          transformedAprAssumptions[loanType] = (program as any).aprAssumptions;
        }
        if (Array.isArray((program as any)?.feeItems)) {
          transformedFeeItems[loanType] = (program as any).feeItems;
        }
        if (Array.isArray((program as any)?.calculatedFeeRules)) {
          transformedCalculatedFeeRules[loanType] = (
            program as any
          ).calculatedFeeRules;
        }
      });
      dispatch(loadAprAssumptionsFromBackend(transformedAprAssumptions));
      dispatch(loadFeeItemsFromBackend(transformedFeeItems));
      dispatch(
        loadCalculatedFeeRulesFromBackend(transformedCalculatedFeeRules)
      );
    }
  }, [dispatch, rateMatrices]);

  useEffect(() => {
    if (productId && product?.refinance) {
      dispatch(
        setRefinanceState({
          currentLoan: {
            rate:
              product.refinance.currentLoan?.rate ?? refinance.currentLoan.rate,
            balance:
              product.refinance.currentLoan?.balance ??
              refinance.currentLoan.balance,
            termYears:
              product.refinance.currentLoan?.termYears ??
              refinance.currentLoan.termYears,
            monthlyPayment:
              product.refinance.currentLoan?.monthlyPayment ??
              refinance.currentLoan.monthlyPayment,
            escrowTaxes:
              product.refinance.currentLoan?.escrowTaxes ??
              refinance.currentLoan.escrowTaxes,
            escrowInsurance:
              product.refinance.currentLoan?.escrowInsurance ??
              refinance.currentLoan.escrowInsurance,
            escrowHoa:
              product.refinance.currentLoan?.escrowHoa ??
              refinance.currentLoan.escrowHoa,
          },
          property: {
            address:
              product.refinance.property?.street ?? refinance.property.address,
            city: product.refinance.property?.city ?? refinance.property.city,
            state:
              product.refinance.property?.state ?? refinance.property.state,
            zipCode:
              product.refinance.property?.zip ?? refinance.property.zipCode,
            value:
              product.refinance.property?.value ?? refinance.property.value,
            propertyType:
              product.refinance.property?.propertyType ??
              refinance.property.propertyType,
          },
          newLoan: {
            termYears:
              product.refinance.newLoan?.termYears ??
              refinance.newLoan.termYears,
            creditScore:
              product.refinance.newLoan?.creditScore ??
              refinance.newLoan.creditScore,
            cashOutAmount:
              product.refinance.newLoan?.cashOutAmount ??
              refinance.newLoan.cashOutAmount,
            requestedLoanAmount:
              product.refinance.newLoan?.requestedLoanAmount ??
              refinance.newLoan.requestedLoanAmount,
          },
          selection: {
            program:
              (product.refinance.selection?.program as USER_LOAN_PROGRAM) ??
              refinance.selection.program,
            refiType:
              (product.refinance.selection?.refiType as any) ??
              refinance.selection.refiType,
          },
        })
      );
    }
  }, [dispatch, product, productId]);

  const newLoanAmount = useMemo(() => {
    if (
      ['heloc', 'heloan', 'second_mortgage'].includes(
        refinance.selection.refiType
      )
    ) {
      return (
        refinance.newLoan.requestedLoanAmount || refinance.currentLoan.balance
      );
    }

    if (refinance.selection.refiType === 'cash_out') {
      return (
        refinance.currentLoan.balance + (refinance.newLoan.cashOutAmount || 0)
      );
    }

    return refinance.currentLoan.balance;
  }, [
    refinance.currentLoan.balance,
    refinance.newLoan.cashOutAmount,
    refinance.newLoan.requestedLoanAmount,
    refinance.selection.refiType,
  ]);

  const estimationInput = useMemo(() => {
    const equityLineOrLoan =
      refinance.newLoan.requestedLoanAmount || refinance.currentLoan.balance;
    return {
      offerPrice: refinance.property.value,
      listPrice: refinance.property.value,
      downPayment: Math.max(0, refinance.property.value - newLoanAmount),
      zipCode: Number(refinance.property.zipCode) || 0,
      hoaFees: refinance.currentLoan.escrowHoa,
      commission: 0,
      marketInterestRate: refinance.currentLoan.rate,
      creditScore: refinance.newLoan.creditScore,
      loanTerm: refinance.newLoan.termYears,
      currentLoanBalance: refinance.currentLoan.balance,
      requestedLoanAmount: equityLineOrLoan,
      requestedCreditLine: equityLineOrLoan,
    };
  }, [
    refinance.currentLoan.balance,
    refinance.currentLoan.escrowHoa,
    refinance.currentLoan.rate,
    refinance.newLoan.creditScore,
    refinance.newLoan.requestedLoanAmount,
    refinance.newLoan.termYears,
    refinance.property.value,
    refinance.property.zipCode,
    newLoanAmount,
  ]);

  const estimation = useMemo(() => {
    const state = estimationState as RootState;
    switch (refinance.selection.program) {
      case USER_LOAN_PROGRAM.CONVENTIONAL:
        return calculateConventionalClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.FHA:
        return calculateFHAClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.VA:
        return calculateVAClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.USDA:
        return calculateUSDAClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.DSCR:
        return calculateDSCRClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.FIX_AND_FLIP:
        return calculateFixAndFlipClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.COMMERCIAL:
        return calculateCommercialClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.BRIDGE: {
        const totalLoan =
          estimationInput.offerPrice - estimationInput.downPayment;
        const existingLoanBalance = refinance.currentLoan.balance;
        const cashOutAmount = Math.max(0, totalLoan - existingLoanBalance);
        const loanTermMonths = Math.min(
          24,
          Math.max(6, estimationInput.loanTerm * 12)
        );
        return calculateBridgeRefinanceClosingDisclosure(
          {
            currentPropertyValue: estimationInput.offerPrice,
            existingLoanBalance,
            cashOutAmount,
            zipCode: estimationInput.zipCode,
            hoaFees: estimationInput.hoaFees,
            marketInterestRate: estimationInput.marketInterestRate,
            creditScore: estimationInput.creditScore,
            loanTerm: loanTermMonths,
          },
          state
        );
      }
      case USER_LOAN_PROGRAM.HELOC:
        return calculateHELOCClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.HELOAN:
        return calculateHELOANClosingDisclosure(estimationInput, state);
      case USER_LOAN_PROGRAM.SECOND_MORTGAGE:
        return calculateSecondMortgageClosingDisclosure(estimationInput, state);
      default:
        return calculateDefaultClosingDisclosure(estimationInput, state);
    }
  }, [
    estimationState,
    estimationInput,
    refinance.selection.program,
    refinance.currentLoan.balance,
  ]);

  const currentMonthlyTotal =
    refinance.currentLoan.monthlyPayment +
    refinance.currentLoan.escrowTaxes +
    refinance.currentLoan.escrowInsurance +
    refinance.currentLoan.escrowHoa;
  const estMetrics = estimation as {
    piti?: number;
    closingCosts?: number;
  } | null;
  const newMonthlyTotal = estMetrics?.piti ?? 0;
  const monthlyDelta = currentMonthlyTotal - newMonthlyTotal;
  const closingCosts = estMetrics?.closingCosts ?? 0;
  const breakevenMonths =
    monthlyDelta > 0 ? Math.round(closingCosts / monthlyDelta) : null;

  const loanType = mapUserLoanProgramToLoanType(refinance.selection.program);
  const backendProgramKey =
    FRONTEND_TO_BACKEND_LOAN_TYPE[
      loanType as keyof typeof FRONTEND_TO_BACKEND_LOAN_TYPE
    ];
  const programDocs =
    (rateMatrices?.programs?.[backendProgramKey] as any)?.refinanceDocs || {};
  const checklist = programDocs?.[refinance.selection.refiType] || [];

  const missingInputs =
    refinance.property.value <= 0 || refinance.currentLoan.balance <= 0;

  const productForLoanEstimate = useMemo(() => {
    if (
      !product ||
      product.transactionMode !== 'refinance' ||
      !product.refinance
    ) {
      return null;
    }
    return mergeRefinanceSliceIntoProduct(product, refinance);
  }, [product, refinance]);

  const loanEstimateComparisons = useMemo(() => {
    if (!productForLoanEstimate) return [];
    return [
      {
        loanProgram: refinance.selection.program,
        downPayment: getRefinanceImpliedEquityDown(productForLoanEstimate),
      },
    ];
  }, [productForLoanEstimate, refinance.selection.program]);

  useEffect(() => {
    setRateSnapshots(loadRateSnapshots(productId ?? undefined));
  }, [productId]);

  const handleSaveCurrentRate = () => {
    if (!productId) return;
    const rate = estimation?.interestRate ?? 0;
    const program = refinance.selection.program;
    const next = [
      ...rateSnapshots,
      { date: new Date().toISOString(), rate, program },
    ].slice(-MAX_SNAPSHOTS);
    setRateSnapshots(next);
    saveRateSnapshots(productId, next);
  };

  const sectionCardSx = {
    borderRadius: 3,
    border: `1px solid ${theme.palette.greyShades.border1}`,
    color: 'text.primary',
    boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
  };

  const cardContentSx = {
    p: { xs: 2, sm: 3, md: 4 },
    color: 'text.primary',
  };

  if (productId && isProductLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <Typography variant="body1" color="text.secondary">
          Loading refinance summary...
        </Typography>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 4,
        px: { xs: 2, sm: 3, md: 4 },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            }}
          >
            Refinance Summary
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              mt: 0.5,
            }}
          >
            Compare your current loan to your refinance options.
          </Typography>
        </Box>
        {productId && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              disabled={creatingApprovalApp}
              onClick={async () => {
                try {
                  const app = await createLoanApplication({
                    flowProfile: 'refinance',
                    productType: 'conventional',
                    sourceProductId: productId,
                    approvalChannel: 'refinance_summary',
                  }).unwrap();
                  navigate(`/loan/${app._id}`);
                } catch {
                  toast.error('Could not start mortgage approval application.');
                }
              }}
              startIcon={<i className="fa-light fa-file-lines" />}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            >
              Mortgage approval application
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              onClick={() => navigate(`/refinance/intake/${productId}`)}
              startIcon={<i className="fa-light fa-pen" />}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            >
              Edit intake
            </Button>
          </Box>
        )}
      </Box>

      {missingInputs && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please complete the refinance intake so we can generate a full
          comparison.
          <Button
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() =>
              navigate(
                productId
                  ? `/refinance/intake/${productId}`
                  : '/refinance/intake'
              )
            }
          >
            Go to Intake
          </Button>
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <GlassCard variant="medium" sx={sectionCardSx}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                px: { xs: 2, sm: 3, md: 4 },
                pt: { xs: 2, sm: 3, md: 4 },
                pb: 1,
              }}
            >
              Comparison Analysis
            </Typography>
            <CardContent sx={cardContentSx}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    Current Loan
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      fontSize: { xs: '1rem', sm: '1.125rem' },
                    }}
                  >
                    {formatPercent(refinance.currentLoan.rate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Balance: {formatCurrency(refinance.currentLoan.balance)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Term: {refinance.currentLoan.termYears} years
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monthly: {formatCurrency(currentMonthlyTotal)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    Refinance Estimate
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      fontSize: { xs: '1rem', sm: '1.125rem' },
                    }}
                  >
                    {formatPercent(estimation?.interestRate || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    New Loan: {formatCurrency(newLoanAmount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Term: {refinance.newLoan.termYears} years
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monthly: {formatCurrency(newMonthlyTotal)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2, borderColor: 'divider' }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    Monthly Delta
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: 'text.primary' }}
                  >
                    {formatCurrency(monthlyDelta)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    Estimated Closing Costs
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: 'text.primary' }}
                  >
                    {formatCurrency(closingCosts)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    Break-even (Months)
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: 'text.primary' }}
                  >
                    {breakevenMonths !== null ? breakevenMonths : '—'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </GlassCard>
        </Grid>

        <Grid item xs={12} lg={5}>
          <GlassCard variant="medium" sx={sectionCardSx}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                px: { xs: 2, sm: 3, md: 4 },
                pt: { xs: 2, sm: 3, md: 4 },
                pb: 1,
              }}
            >
              Refinance Details
            </Typography>
            <CardContent sx={cardContentSx}>
              <Typography variant="body2" color="text.secondary">
                Program: {userLoanProgramMap[refinance.selection.program]}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Refi Type: {REFI_TYPE_LABELS[refinance.selection.refiType]}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Property Value: {formatCurrency(refinance.property.value)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Loan-to-Value:{' '}
                {refinance.property.value > 0
                  ? formatPercent(
                      (newLoanAmount / refinance.property.value) * 100
                    )
                  : '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Credit Score: {refinance.newLoan.creditScore}
              </Typography>

              <Divider sx={{ my: 2, borderColor: 'divider' }} />

              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => setShowDocs(true)}
                sx={{
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Move Forward
              </Button>
            </CardContent>
          </GlassCard>
        </Grid>

        <Grid item xs={12}>
          <GlassCard variant="medium" sx={sectionCardSx}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.125rem' },
                px: { xs: 2, sm: 3, md: 4 },
                pt: { xs: 2, sm: 3, md: 4 },
                pb: 1,
              }}
            >
              Rate tracking
            </Typography>
            <CardContent sx={cardContentSx}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Save the current estimated rate to track how it changes over
                time and see when it&apos;s a good time to refinance.
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                size="medium"
                startIcon={<i className="fa-light fa-chart-line" />}
                onClick={handleSaveCurrentRate}
                disabled={missingInputs || estimation?.interestRate == null}
                sx={{ mb: 2, fontWeight: 600, borderRadius: 2 }}
              >
                Save current rate
              </Button>
              {rateSnapshots.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No rate snapshots yet. Save the current rate above to start
                  tracking.
                </Typography>
              ) : (
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {[...rateSnapshots].reverse().map((s, i) => (
                    <Box component="li" key={`${s.date}-${i}`} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(s.date).toLocaleDateString(undefined, {
                          dateStyle: 'medium',
                        })}
                        {' — '}
                        {formatPercent(s.rate)}
                        {s.program != null &&
                        userLoanProgramMap[
                          s.program as keyof typeof userLoanProgramMap
                        ] != null
                          ? ` (${
                              userLoanProgramMap[
                                s.program as keyof typeof userLoanProgramMap
                              ]
                            })`
                          : ''}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </GlassCard>
        </Grid>

        {!missingInputs && productForLoanEstimate && (
          <Grid item xs={12}>
            <Box sx={{ mt: { xs: 1, md: 2 } }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 2,
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                }}
              >
                Loan estimate
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Detailed closing cost breakdown for your selected refinance
                program ({userLoanProgramMap[refinance.selection.program]}).
              </Typography>
              <ProductDetailEstimation
                product={productForLoanEstimate}
                comparisons={loanEstimateComparisons}
              />
            </Box>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={showDocs}
        onClose={() => setShowDocs(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Documentation Checklist</DialogTitle>
        <DialogContent dividers>
          {checklist.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No program-specific documents are configured yet.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {checklist.map((item: string, index: number) => (
                <FormControlLabel
                  key={`${item}-${index}`}
                  control={<Checkbox />}
                  label={item}
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDocs(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
